import * as React from 'react';
import {Form, Modal} from 'antd';
import {FormComponentProps} from 'antd/lib/form';

import {ErrorResponse, InventoryStockItem, SaveInventoryStockItemCostRequest,} from '../api/dtos/dtos';
import locales from '../locales';
import NumberFormItem from '../common/form-items/NumberFormItem';
import {ErrorAlert} from '../common/ErrorAlert';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay,
} from '../app/ApplicationContext';
import furoApi from '../api/FuroApi';
import {numberGroupFormatter, numberGroupParser, renderFixedPriceColumn} from '../utils/number-utils';
import FormItem from "antd/lib/form/FormItem";
import * as formUtils from "../utils/form-utils";
import {CURRENCY_HUF} from "../utils/currency-utils";

const FIELD_COST = 'cost';
const FIELD_PROFIT_MARGIN = 'profitMargin';

interface Props extends FormComponentProps {
  item: InventoryStockItem;
  onCancel: () => void;
  onConfirm: () => void;
}

interface State {
  error?: ErrorResponse;
}

class InventoryStockItemEditModal extends React.Component<Props, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    error: undefined,
  };

  public render(): React.ReactNode {
    const {item, form, onCancel} = this.props;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Modal
          visible={true}
          title={locales.edit_entry}
          okText={locales.save}
          cancelText={locales.cancel}
          onOk={this.onOk}
          onCancel={onCancel}
          destroyOnClose={true}
        >
          <Form>
            <NumberFormItem
              form={form}
              fieldId={FIELD_COST}
              label={locales.initial_cost}
              initialValue={item.hufCost}
              required={true}
              min={0}
              precision={0}
              parser={numberGroupParser}
              formatter={numberGroupFormatter}
            >
              <span style={{marginLeft: 8}}>{locales.app_currency}</span>
            </NumberFormItem>

            <NumberFormItem
              form={form}
              fieldId={FIELD_PROFIT_MARGIN}
              label={locales.profit_margin_percent}
              initialValue={item.profitMargin}
              required={true}
              min={0}
              precision={0}
              parser={numberGroupParser}
              formatter={numberGroupFormatter}
            >
              <> %</>
            </NumberFormItem>

            <FormItem {...formUtils.formItemLayout} label={locales.selling_price}>
              <span className="ant-form-text">{renderFixedPriceColumn(this.getSellingPrice())}</span>
            </FormItem>
          </Form>

          <ErrorAlert style={{marginTop: 16}} error={this.state.error}/>
        </Modal>
      </>
    );
  }

  private getSellingPrice = (): number => {
    const {item, form} = this.props;

    const cost = form.getFieldValue(FIELD_COST) ?? item.hufCost;
    const profitMargin = form.getFieldValue(FIELD_PROFIT_MARGIN) ?? item.profitMargin;

    const multiplier = item.currency === CURRENCY_HUF ? 1 : (item.exchangeRate != null ? item.exchangeRate : 1);
    const costWithExchangeRate = cost * multiplier;
    return costWithExchangeRate * (1 + profitMargin / 100);
  };

  private onOk = () => {
    const {item, form, onConfirm} = this.props;

    if (!item.id) {
      this.setState({error: {message: locales.save_error}});
      return;
    }

    form.validateFields([FIELD_COST, FIELD_PROFIT_MARGIN], (err, values) => {
      if (err) {
        return;
      }

      const request: SaveInventoryStockItemCostRequest = {
        cost: values[FIELD_COST],
        profitMargin: values[FIELD_PROFIT_MARGIN],
      };

      const promise = (furoApi.inventoryStockItems as any).save(item.id, request)
        .then(onConfirm)
        .catch(this.onApiError);

      withLoadingOverlay(promise, this.loadingHandler);
    });
  };

  private onApiError = (error: ErrorResponse) => this.setState({error});
}

export default Form.create<Props>()(InventoryStockItemEditModal);





// WEBPACK FOOTER //
// ./src/inventory-stock-details/InventoryStockItemEditModal.tsx